import {GROUND_BACKGROUNDS} from '../groundBackgrounds';

/**
 * ПЫЛЕВОЙ ВИХРЬ — эффект красной подложки (red_1).
 *
 * По растрескавшемуся дну котловины бродит пылевой вихрь: светлое тело в пару
 * ячеек шириной, которое все время меняет очертание, — клубы пыли кружат в нем
 * друг вокруг друга, а с края срываются пряди и тают, уходя от вихря по спирали.
 * Позади остается длинная полоса пыли: она висит и после того, как вихрь сошел
 * на нет. От вихря на землю падает тень столба — короткое размытое
 * пятно-воронка: темнее всего под вихрем, оно расходится и тает в сторону и
 * покачивается со столбом (см. utils/effects/biome/dustDevil). Вихрь идет по
 * ветру матча — тому же, что несет облака, — и петляет.
 *
 * Колец в вихре нет: кольцо размером с ячейку читалось бы игровой меткой.
 *
 * ВИХРЬ ИДЕТ НАД ВСЕМ ПОЛЕМ — над жетонами строений, их значками, отрядами и
 * шевронами отрядов (слой AIR): это столб пыли в воздухе, и под жетонами он
 * смотрелся пятном на земле. Так решил владелец игры; тень столба падает
 * туда же, поверх всего, как тени облаков. Поэтому все в вихре мягкое и
 * полупрозрачное: под ним поле обязано читаться.
 *
 * У ВИХРЯ ЕСТЬ ЗВУК — вой ветра с песком (DUST_DEVIL_SOUND_ID, отложенный
 * звук, см. configs/deferredSounds: качается в фоне после загрузки матча).
 * Вихрь не рождается, пока звук не загружен: картинка и звук идут только
 * вместе, и оба плавно нарастают и плавно стихают (scenes/FieldAmbience).
 */
export const DUST_DEVIL_KIND = 'dustDevil';

/** Звук вихря: имя отложенного звука (configs/deferredSounds) */
export const DUST_DEVIL_SOUND_ID = 'storm_desert_wind';

export const DUST_DEVIL_EFFECT = Object.freeze({
  kind: DUST_DEVIL_KIND,
  backgrounds: Object.freeze({
    [GROUND_BACKGROUNDS.RED_1]: Object.freeze({
      // Первый вихрь — вскоре после начала; следующий — через паузу от рождения
      // предыдущего. Пауза обычно длиннее жизни вихря: вихрь на поле один, и
      // лишь изредка новый рождается, пока прежний еще тает
      firstDelayMs: {min: 3000, max: 9000},
      intervalMs: {min: 22000, max: 42000},
      // Групп, а не вихрей: группа — это вихрь И ЕГО ШЛЕЙФ, она живет, пока не
      // растает последний клуб пыли, — жизнь вихря и еще срок клуба, до 46
      // секунд. Почти всегда групп на поле одна-две: вихрь со своим шлейфом и
      // доживающий шлейф прежнего. Третья нужна редко — когда после
      // вихря-долгожителя выпали две самые короткие паузы подряд (44 секунды):
      // без нее доживающий шлейф задержал бы новый вихрь. Самих вихрей разом
      // по-прежнему не больше двух: третий рождается не раньше, чем через две
      // паузы после первого, а столько вихрь не живет
      maxGroups: 3,
      // Сколько вихрь живет — вместе с ростом в начале и угасанием в конце.
      // Звук вихря длится 33 секунды: вихрь обязан кончиться раньше него
      lifeMs: {min: 16000, max: 26000},
      // За сколько вихрь вырастает в начале и сходит на нет в конце. Быстрее —
      // вихрь двух ячеек в ширину не растет, а выскакивает
      growMs: 2600,
      fadeMs: 3600,
      // Вихрь растет и сходит на нет РАЗМЕРОМ ВМЕСТЕ С ПЛОТНОСТЬЮ (просьба
      // владельца игры: прежде в конце он стоял почти в полный рост и просто
      // становился прозрачным). Доля полного размера в миг рождения и в миг,
      // когда плотность дошла до нуля: вихрь сворачивается в точку
      growFromScale: 0.25,
      fadeToScale: 0.2,
      // Скорость вдоль курса, размеров ячейки в секунду: вихрь бредет, а не летит
      speedFactor: {min: 0.55, max: 0.9},
      // Курс — по ветру матча, с отклонением не больше стольких градусов
      headingSpreadDeg: 40,
      // Сколько путей перебирается для нового вихря: берется тот, что целиком
      // лежит на дне котловины. Вихрю вдвое шире нужно больше попыток, чем
      // прежнему (было 6): дно для него уже (см. floor), и с прежним числом
      // каждый двадцатый вихрь шестую часть жизни гас бы у кромки
      pathAttempts: 10,
      // Вихрь петляет поперек курса: две волны с разным размахом (размеров
      // ячейки) и периодом складываются в неровный путь
      meanders: Object.freeze([
        {amplitudeFactor: {min: 0.3, max: 0.55}, periodMs: {min: 4500, max: 5500}},
        {amplitudeFactor: {min: 0.6, max: 1}, periodMs: {min: 8000, max: 10000}},
      ]),
      // Ядро — светлый клуб пыли в середине вихря. Размер кадра клуба, размеров
      // ячейки; плотное пятно — около половины кадра (ячейка-полторы в ширину),
      // дальше — редкая кайма. Поперечник вдвое больше прежнего (просьба
      // владельца игры), и вместе с ядром выросло все, что задано его долями:
      // доли тела, пряди, клубы шлейфа. Такой вихрь закрывает три-четыре ячейки
      // и идет над отрядами, поэтому тело полупрозрачно: жетон и шеврон под
      // вихрем читаются. Но и не прозрачнее: бледный вихрь в игре выходил
      // дымкой, которую легко не заметить.
      //
      // ТЕЛО ВИХРЯ — ядро и доли (lobes) внахлест, поэтому само ядро бледнее,
      // чем было, пока тело состояло из него одного (0.58): остальное добирают
      // доли. Настоящая плотность тела осталась прежней — замер по текстурам:
      // в середине вихря 0.49–0.56 (было 0.58), в самом плотном месте, где на
      // ядро легли доли и прядь, — в среднем 0.64, как и было (от 0.56 до 0.79;
      // было от 0.58 до 0.70 — под штрихом вращения)
      core: {
        sizeFactor: {min: 3.4, max: 4.4},
        tint: 0xf8e2c8,
        alpha: 0.4,
        // Клуб с неровным краем вращается — столб крутится
        spinPeriodMs: 2200,
        // Ядро — не ровный диск: ширина и высота его кадра дышат порознь, каждая —
        // парой волн (share — размах волны, доля размера ядра). Периоды всех
        // четырех волн несоизмеримы: клуб то вытянут, то сплюснут, и ни разу не
        // так, как секунду назад
        stretch: {
          width: [{share: 0.07, periodMs: 1700}, {share: 0.05, periodMs: 2900}],
          height: [{share: 0.07, periodMs: 2300}, {share: 0.05, periodMs: 3700}],
        },
      },
      // Доли тела: клубы поменьше кружат вокруг середины вихря — в ту же
      // сторону, что и все в вихре, но каждая на своем расстоянии и в своем
      // темпе (orbitPeriodMs — оборот вокруг середины, spinPeriodMs — оборот
      // клуба вокруг себя). Одна догоняет другую раз в семь секунд: то они по
      // одну сторону ядра, и тело выпирает туда, то по разные — и оно
      // вытянуто. Размер кадра (sizeShare) и расстояние до середины вихря
      // (orbitShare) — доли размера ядра. Доля не выходит из-под каймы ядра
      // (orbitShare + sizeShare / 2 не больше половины его кадра): тело — один
      // вихрь, а не три клуба рядом, и поперечник вихря прежний
      lobes: Object.freeze([
        {sizeShare: 0.72, orbitShare: 0.13, orbitPeriodMs: 2300, spinPeriodMs: 1600, alpha: 0.26},
        {sizeShare: 0.56, orbitShare: 0.19, orbitPeriodMs: 3400, spinPeriodMs: 2700, alpha: 0.22},
      ]),
      // Пряди: клочья пыли, которые вихрь срывает с тела (просьба владельца
      // игры: части, которые меняются и «затухают к периферии, когда они
      // смещаются»). Прядь рождается у тела, уходит от него по раскручивающейся
      // спирали, вытягивается, становится тоньше и тает к краю, а потом
      // рождается снова — под другим углом. Прядь — мягкая дуга (текстура
      // RIM_SEGMENT), выгнутая наружу: она огибает тело и лежит вдоль своей
      // спирали. Прямой тонкий штрих читался бы иглой или скобкой у пятна, клуб —
      // камешком, а штрих поперек спирали — спицей колеса. Все размеры — доли
      // размера ядра
      wisps: {
        count: 8,
        // Круг жизни пряди — от рождения у тела до края. У каждой пряди срок
        // свой, из этого отрезка: пряди рождаются вразнобой и в лад не попадают
        cycleMs: {min: 1500, max: 2700},
        // От какого расстояния до середины вихря прядь рождается и до какого
        // доходит. Рождается под плотным пятном ядра; внешний радиус — на
        // четверть дальше каймы ядра (половина его кадра): пряди выходят за
        // прежний край вихря на восьмую долю его кадра — полразмера ячейки, — и
        // там их уже не видно
        radiusShare: {from: 0.13, to: 0.625},
        // С какого расстояния прядь начинает таять: это край плотного пятна
        // тела — там плотность пряди полная, — у внешнего радиуса она ноль
        fadeFromShare: 0.24,
        // Сколько оборотов вокруг вихря прядь делает за свой круг
        turns: {min: 0.5, max: 0.85},
        // Длина кадра дуги в начале и в конце пути: прядь вытягивается (видимая
        // дуга — около половины кадра)...
        lengthShare: {from: 0.42, to: 0.8},
        // ...и становится тоньше. Высота кадра дуги; видимый гребень впятеро
        // уже. Длина и высота кадра задают и изгиб дуги (радиус изгиба — 0.43
        // длины в квадрате, деленной на высоту): у тела — 0.15 размера ядра, у
        // края — 0.73. Это почти расстояние от пряди до середины вихря: дуга
        // огибает вихрь по своему кругу
        thicknessShare: {from: 0.5, to: 0.38},
        tint: 0xfff3e0,
        alpha: 0.36,
        // Какую долю круга прядь проступает: рождается она не скачком, а под
        // плотным пятном ядра, и в полную плотность выходит у его края. Быстрее —
        // и под ядром вспыхивала бы светлая черточка
        riseShare: 0.2,
      },
      // Шлейф: клубы пыли остаются на пути вихря. Клуб рождается под вихрем,
      // лежит на месте, растет и редеет, а потом рождается снова — уже там, куда
      // вихрь ушел. Между рождениями соседних клубов — lagMs; клуб живет
      // count × lagMs — двадцать секунд.
      //
      // ПЫЛЬ ВИСИТ ДОЛГО И ГУЩЕ ПРЕЖНЕГО (просьба владельца игры: «это ж смерч,
      // после него не сразу пыль пропадает»; было 12 клубов через 750 мс —
      // девять секунд — с плотностью 0.24). За двадцать секунд вихрь уходит на
      // 11–18 размеров ячейки: полоса пыли тянется за ним почти на весь его
      // путь. И вихрь она ПЕРЕЖИВАЕТ: он сошел на нет, а полоса доживает свой
      // срок и тает сама.
      //
      // Клубы лежат внахлест, и настоящая плотность полосы — не плотность
      // одного клуба. Замер по текстурам на пути вихря: у самого вихря — 0.5
      // (у прежнего шлейфа 0.4), через 8 секунд — 0.4 (было 0.1), через 12 —
      // 0.2, через 16 — 0.05; после конца вихря самое плотное место полосы —
      // 0.6, через 5 секунд — 0.4, через 12 — 0.1. Голова вихря плотнее полосы
      // (в его середине, вместе с клубами под телом, — 0.63): она остается
      // самым светлым местом вихря
      plume: {
        // Реже клубы класть нельзя: при 1000 мс и больше между ними в полосе
        // случаются прорехи — клуб ложится не точно на путь (jitterShare)
        count: 22,
        lagMs: 900,
        // Размер клуба в начале и в конце его жизни, доли размера ядра. Соседние
        // клубы лежат в половине-трех четвертях размера ячейки друг от друга, а
        // сами — в несколько ее размеров: шлейф — сплошная полоса пыли, а не
        // ряд шаров
        sizeShare: {from: 0.75, to: 2},
        // Пыль расходится быстро, пока свежа, и все медленнее потом: размер
        // растет как возраст в этой степени (1 — ровно)
        spreadPower: 0.7,
        tint: 0xf8e2c8,
        // Плотность свежего клуба — у самого вихря. Шлейф идет над отрядами,
        // поэтому густ он только там: дальше клуб редеет (fadePower), и под
        // старой частью полосы поле читается, как под дымкой
        alpha: 0.36,
        // За сколько клуб проступает — под телом вихря, пока тот не ушел
        riseMs: 900,
        // Чем больше степень, тем раньше клуб редеет: к середине жизни от
        // плотности остается меньше трети, дальше — долгий бледный хвост
        fadePower: 1.8,
        // Клуб ложится не точно на путь (доля размера ядра) и медленно поворачивается
        jitterShare: 0.14,
        turnDeg: 70,
        // Висящую пыль понемногу сносит ветер матча: размеров ячейки в секунду.
        // За жизнь клуба — меньше размера ячейки: пыль оседает, а не летит
        driftFactor: 0.04,
      },
      // Тень столба — короткое размытое пятно-воронка: от основания вихря в
      // сторону, куда на картинке падают тени гор (вниз и чуть влево). Темнее
      // всего тень под вихрем, дальше расходится и плавно сходит на нет; четкой
      // кромки у нее нет нигде (текстура FUNNEL). Просьбы владельца игры: тень
      // короче и светлее; потом — еще вдвое короче и гораздо размытее
      shadow: {
        tint: 0x3e1c12,
        // Плотность самого темного места тени — как у прежней, четкой; все
        // остальное пятно светлее
        alpha: 0.32,
        // Длина тени от вихря до места, где она сошла на нет, доля размера
        // ядра. Было 1.3: относительно вихря тень короче в два с лишним раза, а
        // на поле — при вдвое большем ядре — тень самого крупного вихря короче
        // прежней тени самого мелкого. Из-под плотного пятна ядра (треть кадра
        // от середины) она выходит на половину размера ячейки
        lengthShare: 0.5,
        // Высота кадра тени, доля размера ядра; видимое пятно у дальнего конца
        // вдвое уже кадра — чуть уже плотного пятна ядра
        widthShare: 0.8,
        // Где в кадре текстуры FUNNEL лежит тень, доли длины кадра от его
        // левого края: основание (его ставят под вихрь) и дальний конец. Слева
        // от основания — запас, на котором тень сходит на нет: поэтому кадр
        // длиннее самой тени. Числа обязаны сходиться с текстурой (см.
        // utils/effects/fieldBiomeTextures) — это сверяют тесты вихря
        frame: {baseShare: 0.2, endShare: 0.95},
        angleDeg: 100,
        // Столб покачивается, и тень ходит вокруг основания вихря: на столько
        // градусов в обе стороны и с таким периодом
        swayDeg: 8,
        swayPeriodMs: {min: 2600, max: 3800},
      },
      // Ровное дно котловины на картинке пейзажа (доли ее стороны): на гребнях
      // вокруг светлый клуб смотрелся бы браком отрисовки, туда вихрь не заходит
      basin: {u: 0.47, v: 0.5, radiusU: 0.27, radiusV: 0.25},
      // Вихрь — не точка: его плотное пятно расходится от середины на такую
      // долю размера ядра (bodyReachShare). На столько же овал дна для вихря
      // уже нарисованного: у кромки дна гаснет не середина вихря, а его край —
      // и вихрь в пару ячеек шириной на гребни не ложится. minShare — меньше
      // этой доли нарисованного овал не станет и на крошечном поле
      floor: {bodyReachShare: 0.3, minShare: 0.5},
      // Середина пути вихря — не дальше этой доли полуосей дна для вихря от
      // его середины
      basinInnerShare: 0.7,
      // ...и не ближе стольких размеров ячейки к краю кадра камеры
      viewInsetFactor: 2,
      // У кромки дна для вихря он гаснет: спад на этой доле полуосей
      basinFade: 0.2,
    }),
  }),
});
